import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import FaceOutlinedIcon from '@mui/icons-material/FaceOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GradeOutlinedIcon from '@mui/icons-material/GradeOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { useNavigate } from 'react-router-dom';

const DONUT_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#10b981', '#3b82f6'];

const MODULE_DEFINITIONS = [
  // Academic & Student Modules
  {
    key: 'attendance',
    label: 'Student Attendance',
    shortLabel: 'Attendance',
    category: 'academic',
    schemaColumn: 'student_attendance_last_used_value',
    icon: EventAvailableOutlinedIcon,
    accentColor: '#2563eb',
  },
  {
    key: 'homework',
    label: 'Homework / Diary',
    shortLabel: 'Homework',
    category: 'academic',
    schemaColumn: 'student_diary_last_used_value',
    icon: MenuBookOutlinedIcon,
    accentColor: '#059669',
  },
  {
    key: 'exam',
    label: 'Exam Schedule',
    shortLabel: 'Exam Schedule',
    category: 'academic',
    schemaColumn: 'exam_schedule_last_used_value',
    icon: FactCheckOutlinedIcon,
    accentColor: '#7c3aed',
  },
  {
    key: 'examMarks',
    label: 'Exam Marks',
    shortLabel: 'Exam Marks',
    category: 'academic',
    schemaColumn: 'exam_marks_last_used_value',
    icon: GradeOutlinedIcon,
    accentColor: '#db2777',
  },

  // Operations & Administrative Modules
  {
    key: 'feeCollection',
    label: 'Fee Collection',
    shortLabel: 'Fee Collection',
    category: 'operations',
    schemaColumn: 'fee_collection_last_used_value',
    icon: AccountBalanceWalletOutlinedIcon,
    accentColor: '#0d9488',
  },
  {
    key: 'staffAttendance',
    label: 'Staff Attendance',
    shortLabel: 'Staff Attendance',
    category: 'operations',
    schemaColumn: 'staff_attendance_last_marked_value',
    icon: BadgeOutlinedIcon,
    accentColor: '#dc2626',
  },
  {
    key: 'store',
    label: 'Item Sold / Store',
    shortLabel: 'Store & Inventory',
    category: 'operations',
    schemaColumn: 'item_sold_last_marked_value',
    icon: StorefrontOutlinedIcon,
    accentColor: '#ca8a04',
  },
  {
    key: 'expenses',
    label: 'School Expenses',
    shortLabel: 'Expenses',
    category: 'operations',
    schemaColumn: 'expenses_last_marked_value',
    icon: ReceiptLongOutlinedIcon,
    accentColor: '#b45309',
  },
];

const NEVER_USED_MODULE_MAP = Object.fromEntries(
  MODULE_DEFINITIONS.map((m) => [m.key, m.schemaColumn])
);

const isFutureAcademicYear = (tabName) => {
  if (!tabName) return false;
  const match = String(tabName).match(/\b(19\d\d|20\d\d)\b/);
  if (!match) return false;
  const startYear = parseInt(match[1], 10);
  const now = new Date();
  const currentAcadStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1; // April start
  return startYear > currentAcadStart;
};

function ClientReportDashboard(props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Filter States
  const [syncDates, setSyncDates] = React.useState([]);
  const [syncDateFilter, setSyncDateFilter] = React.useState('');
  const [syncDatesReady, setSyncDatesReady] = React.useState(false);
  const [schoolFilter, setSchoolFilter] = React.useState('');
  const [schoolSearchText, setSchoolSearchText] = React.useState('');
  const [schoolOptions, setSchoolOptions] = React.useState([]);
  const [schoolOptionsLoading, setSchoolOptionsLoading] = React.useState(false);
  const [appliedSchoolFilter, setAppliedSchoolFilter] = React.useState('');
  const [academicYearFilter, setAcademicYearFilter] = React.useState('');
  const [clientStatusTab, setClientStatusTab] = React.useState('active');
  const [creationTab, setCreationTab] = React.useState('created');

  // Main Dashboard Data
  const [dashboardData, setDashboardData] = React.useState({
    neverUsedCounts: {},
    rows: [],
    columns: [],
    tabs: [],
    pocSummary: [],
    statusCounts: {},
  });
  const [loading, setLoading] = React.useState(false);

  // In-Page Quick Schools Drawer States
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedModule, setSelectedModule] = React.useState(null);
  const [drawerUsageType, setDrawerUsageType] = React.useState('never_used');
  const [drawerSchools, setDrawerSchools] = React.useState([]);
  const [drawerLoading, setDrawerLoading] = React.useState(false);
  const [drawerSearch, setDrawerSearch] = React.useState('');

  // Fetch sync dates on mount and default to latest
  React.useEffect(() => {
    getRequest(
      GET_URL.client_track_report.api,
      { sync_dates: 1 },
      props
    ).then((response) => {
      if (response?.status === 200) {
        const dates = response.data?.data || [];
        setSyncDates(dates);
        setSyncDateFilter((current) => {
          if (current && dates.some((d) => d.date === current)) return current;
          return dates[0]?.date || '';
        });
      }
      setSyncDatesReady(true);
    }).catch(() => setSyncDatesReady(true));
  }, [props]);

  const fetchDashboard = React.useCallback(() => {
    if (!syncDatesReady) return;
    setLoading(true);
    const params = {
      dashboard: 1,
      status_by_team: clientStatusTab,
      creation_status: creationTab,
      ...(syncDateFilter && { sync_date: syncDateFilter }),
      ...(appliedSchoolFilter.trim() && { school: appliedSchoolFilter.trim() }),
      ...(academicYearFilter && { tab_name: academicYearFilter }),
    };
    getRequest(
      GET_URL.client_track_report.api,
      params,
      props
    ).then((response) => {
      setLoading(false);
      if (response?.status === 200) {
        setDashboardData(response.data?.data || {});
      }
    }).catch(() => setLoading(false));
  }, [appliedSchoolFilter, academicYearFilter, clientStatusTab, creationTab, syncDateFilter, syncDatesReady, props]);

  React.useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Autocomplete School Options
  const fetchSchoolOptions = React.useCallback((searchText = '') => {
    setSchoolOptionsLoading(true);
    getRequest(
      GET_URL.client_track_report.api,
      {
        institute_options: 1,
        ...(searchText.trim() && { search: searchText.trim() }),
      },
      props
    ).then((response) => {
      setSchoolOptionsLoading(false);
      if (response?.status === 200) {
        setSchoolOptions(response.data?.data || []);
      }
    }).catch(() => setSchoolOptionsLoading(false));
  }, [props]);

  React.useEffect(() => {
    fetchSchoolOptions(schoolSearchText);
  }, [fetchSchoolOptions, schoolSearchText]);

  // Derived Metrics
  const neverUsedCounts = dashboardData.neverUsedCounts || {};
  const totalSchools = Number(dashboardData.totalSchools || 0);
  const statusCounts = dashboardData.statusCounts || {};
  const pocData = dashboardData.pocSummary || [];
  const availableTabs = (dashboardData.tabs || [])
    .filter((tab) => !isFutureAcademicYear(tab))
    .sort((a, b) => {
      const ma = String(a).match(/\b(19\d\d|20\d\d)\b/);
      const mb = String(b).match(/\b(19\d\d|20\d\d)\b/);
      const ya = ma ? parseInt(ma[1], 10) : 0;
      const yb = mb ? parseInt(mb[1], 10) : 0;
      return yb - ya;
    });

  // Calculate Overall Platform Adoption Rate %
  const totalModuleOpportunities = totalSchools * MODULE_DEFINITIONS.length;
  const totalNeverUsed = MODULE_DEFINITIONS.reduce(
    (acc, m) => acc + (Number(neverUsedCounts[m.key]) || 0),
    0
  );
  const overallAdopted = Math.max(0, totalModuleOpportunities - totalNeverUsed);
  const overallAdoptionRate = totalModuleOpportunities > 0
    ? Math.round((overallAdopted / totalModuleOpportunities) * 100)
    : 0;

  // Donut chart dataset
  const neverUsedData = MODULE_DEFINITIONS.map((m) => ({
    name: m.shortLabel,
    value: Number(neverUsedCounts[m.key]) || 0,
    moduleKey: m.key,
  })).filter((item) => item.value > 0);

  // Quick In-Page Drawer Opener (supports both 'used' and 'never_used')
  const handleOpenModuleDrawer = (modDef, usageType = 'never_used') => {
    setSelectedModule(modDef);
    setDrawerUsageType(usageType);
    setDrawerOpen(true);
    setDrawerSearch('');
    setDrawerLoading(true);

    const isUsed = usageType === 'used';
    const params = {
      module_filter: modDef.schemaColumn,
      module_value: isUsed ? 'Used' : 'Never Used',
      limit: 100,
      pageno: 1,
      ...(syncDateFilter && { sync_date: syncDateFilter }),
      ...(academicYearFilter && { tab_name: academicYearFilter }),
      ...(clientStatusTab && clientStatusTab !== 'all' && { status_by_team: clientStatusTab }),
      ...(creationTab && { creation_status: creationTab }),
      ...(appliedSchoolFilter && { school: appliedSchoolFilter }),
    };

    getRequest(
      GET_URL.client_track_report.api,
      params,
      props
    ).then((res) => {
      setDrawerLoading(false);
      if (res?.status === 200) {
        setDrawerSchools(res.data?.data || []);
      }
    }).catch(() => {
      setDrawerLoading(false);
      setDrawerSchools([]);
    });
  };

  // Navigate to full report table
  const handleGoToFullReport = (schemaColumn, moduleValue) => {
    const params = new URLSearchParams();
    if (schemaColumn) {
      params.set('module', schemaColumn);
      if (moduleValue) {
        params.set('module_value', moduleValue);
      }
    }
    if (syncDateFilter) params.set('sync_date', syncDateFilter);
    if (academicYearFilter) params.set('tab_name', academicYearFilter);
    if (clientStatusTab && clientStatusTab !== 'all') params.set('status_by_team', clientStatusTab);
    if (creationTab) params.set('creation_status', creationTab);
    if (appliedSchoolFilter) params.set('school', appliedSchoolFilter);
    navigate(`/client-report/report?${params.toString()}`);
  };

  const handleUserCardClick = () => {
    handleGoToFullReport();
  };

  // Filter drawer schools in real-time
  const filteredDrawerSchools = drawerSchools.filter((school) => {
    if (!drawerSearch.trim()) return true;
    const term = drawerSearch.toLowerCase();
    const name = (school.institute_name || '').toLowerCase();
    const coord = (school.coordinator_name || '').toLowerCase();
    const addr = (school.school_address || '').toLowerCase();
    return name.includes(term) || coord.includes(term) || addr.includes(term);
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1440, mx: 'auto' }}>
      {/* ============================================================== */}
      {/* 1. STICKY FILTER & SEARCH TOOLBAR                              */}
      {/* ============================================================== */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: { xs: 66, sm: 66 },
          zIndex: 90,
          p: { xs: 1.25, sm: 2 },
          mb: 2.5,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.08)',
        }}
      >
        {/* Header Title & Direct Actions */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
          mb={1.5}
        >
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={800} color="#0f172a">
                Client Report Dashboard
              </Typography>
              {dashboardData.latestTabName && (
                <Chip
                  size="small"
                  label={dashboardData.latestTabName}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    bgcolor: '#eff6ff',
                    color: '#1d4ed8',
                    border: '1px solid #bfdbfe',
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Real-time snapshot of school ERP adoption and module usage
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDashboard}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, color: '#475569' }}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                const params = new URLSearchParams();
                if (syncDateFilter) params.set('sync_date', syncDateFilter);
                if (academicYearFilter) params.set('tab_name', academicYearFilter);
                if (clientStatusTab && clientStatusTab !== 'all') params.set('status_by_team', clientStatusTab);
                params.set('view', 'generator');
                navigate(`/client-report/report?${params.toString()}`);
              }}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                borderColor: '#cbd5e1',
                color: '#0f172a',
                '&:hover': {
                  backgroundColor: '#f1f5f9',
                  borderColor: '#94a3b8',
                },
              }}
            >
              Audit Report Generator
            </Button>
            <Button
              size="small"
              variant="contained"
              endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              onClick={() => handleGoToFullReport()}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                backgroundColor: '#2563eb',
                '&:hover': { backgroundColor: '#1d4ed8' },
              }}
            >
              Open Full Report
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Filters Controls Row */}
        <Grid container spacing={1.5} alignItems="center">
          {/* Sync Date Dropdown */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Sync Date"
              value={syncDateFilter}
              onChange={(e) => setSyncDateFilter(e.target.value)}
              disabled={!syncDates.length}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              {syncDates.length === 0 && <MenuItem value="">No syncs yet</MenuItem>}
              {syncDates.map((d) => (
                <MenuItem key={d.date} value={d.date}>
                  {d.label} {d.saved_count != null ? `(${d.saved_count} records)` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Academic Year */}
          <Grid item xs={6} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Academic Year"
              value={academicYearFilter}
              onChange={(e) => setAcademicYearFilter(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">All (Latest Active)</MenuItem>
              {availableTabs.map((tab) => (
                <MenuItem key={tab} value={tab}>{tab}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* School Search Autocomplete */}
          <Grid item xs={6} sm={6} md={3.5}>
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={schoolOptions}
              value={schoolFilter || null}
              inputValue={schoolSearchText}
              loading={schoolOptionsLoading}
              onChange={(event, value) => {
                const selectedValue = value || '';
                setSchoolFilter(selectedValue);
                setSchoolSearchText(selectedValue);
                setAppliedSchoolFilter(selectedValue);
              }}
              onInputChange={(event, value, reason) => {
                setSchoolSearchText(value || '');
                if (reason === 'clear') {
                  setSchoolFilter('');
                  setAppliedSchoolFilter('');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search School"
                  placeholder="Type name & hit Enter..."
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setSchoolFilter(schoolSearchText);
                      setAppliedSchoolFilter(schoolSearchText);
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {schoolOptionsLoading && <CircularProgress color="inherit" size={16} />}
                        {schoolSearchText && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSchoolSearchText('');
                              setSchoolFilter('');
                              setAppliedSchoolFilter('');
                            }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />
          </Grid>

          {/* Creation Status Segment */}
          <Grid item xs={12} sm={6} md={3.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 0.4,
                gap: 0.5,
              }}
            >
              {[
                { value: 'created', label: 'Created', count: statusCounts.created },
                { value: 'not_created', label: 'Not Created', count: statusCounts.not_created },
              ].map((opt) => (
                <Button
                  key={opt.value}
                  size="small"
                  fullWidth
                  variant={creationTab === opt.value ? 'contained' : 'text'}
                  onClick={() => setCreationTab(opt.value)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1.5,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    py: 0.4,
                    ...(creationTab === opt.value
                      ? {
                          backgroundColor: opt.value === 'not_created' ? '#ea580c' : '#059669',
                          color: '#ffffff',
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: opt.value === 'not_created' ? '#c2410c' : '#047857',
                          },
                        }
                      : {
                          color: '#64748b',
                        }),
                  }}
                >
                  {opt.label} ({opt.count !== undefined ? opt.count : '0'})
                </Button>
              ))}
            </Box>
          </Grid>

          {/* Client Status Filter: All, Active, Low Engagement, At Risk, Dead */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 0.4,
                gap: 0.6,
                overflowX: 'auto',
              }}
            >
              {[
                { value: 'all', label: 'All', count: statusCounts.all, activeColor: '#334155', hoverColor: '#1e293b' },
                { value: 'active', label: 'Active', count: statusCounts.active, activeColor: '#2563eb', hoverColor: '#1d4ed8' },
                { value: 'low_engagement', label: 'Low Eng.', count: statusCounts.low_engagement, activeColor: '#d97706', hoverColor: '#b45309' },
                { value: 'at_risk', label: 'Risk', count: statusCounts.at_risk, activeColor: '#ea580c', hoverColor: '#c2410c' },
                { value: 'dead', label: 'Dead', count: statusCounts.dead, activeColor: '#ef4444', hoverColor: '#dc2626' },
              ].map((opt) => (
                <Button
                  key={opt.value}
                  size="small"
                  variant={clientStatusTab === opt.value ? 'contained' : 'text'}
                  onClick={() => setClientStatusTab(opt.value)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 1.5,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    py: 0.5,
                    px: 1.2,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    flex: 1,
                    ...(clientStatusTab === opt.value
                      ? {
                          backgroundColor: opt.activeColor,
                          color: '#ffffff',
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: opt.hoverColor,
                          },
                        }
                      : {
                          color: '#64748b',
                        }),
                  }}
                >
                  {opt.label} ({opt.count !== undefined ? opt.count : '0'})
                </Button>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ============================================================== */}
      {/* 2. ADOPTION HEALTH SCORE & EXECUTIVE METRICS BANNER           */}
      {/* ============================================================== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Overall Platform Adoption Health Gauge */}
        <Grid item xs={12} lg={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
            }}
          >
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" fontWeight={800} color="#64748b" textTransform="uppercase" letterSpacing={0.5}>
                  Overall Module Adoption
                </Typography>
                <Chip
                  size="small"
                  icon={<TrendingUpIcon style={{ fontSize: 14 }} />}
                  label={
                    overallAdoptionRate >= 70
                      ? 'High Usage'
                      : overallAdoptionRate >= 45
                      ? 'Moderate'
                      : 'Needs Follow-up'
                  }
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    bgcolor:
                      overallAdoptionRate >= 70
                        ? '#dcfce7'
                        : overallAdoptionRate >= 45
                        ? '#fef3c7'
                        : '#fee2e2',
                    color:
                      overallAdoptionRate >= 70
                        ? '#166534'
                        : overallAdoptionRate >= 45
                        ? '#92400e'
                        : '#991b1b',
                  }}
                />
              </Box>

              <Box display="flex" alignItems="baseline" gap={1} my={1}>
                <Typography
                  variant="h3"
                  fontWeight={900}
                  sx={{
                    color:
                      overallAdoptionRate >= 70
                        ? '#15803d'
                        : overallAdoptionRate >= 45
                        ? '#d97706'
                        : '#dc2626',
                  }}
                >
                  {overallAdoptionRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Feature Usage
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={overallAdoptionRate}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    backgroundColor:
                      overallAdoptionRate >= 70
                        ? '#22c55e'
                        : overallAdoptionRate >= 45
                        ? '#f59e0b'
                        : '#ef4444',
                  },
                }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" mt={1.5}>
              Calculated across 8 key modules for <strong>{totalSchools}</strong> institutes.
            </Typography>
          </Paper>
        </Grid>

        {/* 4 Core Vital Cards */}
        <Grid item xs={12} lg={8}>
          <Grid container spacing={1.5}>
            {/* Total Institutes */}
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                onClick={handleUserCardClick}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#2563eb',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 18px rgba(37,99,235,0.12)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                  <SchoolOutlinedIcon sx={{ color: '#2563eb', fontSize: 18 }} />
                  <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.7rem">
                    Institutes
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={900} color="#0f172a">
                  {totalSchools}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" fontSize="0.7rem" sx={{ mt: -0.3, mb: 0.5 }}>
                  {creationTab === 'not_created'
                    ? 'Uncreated for year'
                    : clientStatusTab === 'all'
                    ? `${totalSchools} synced of ${statusCounts.all || totalSchools} total`
                    : clientStatusTab === 'active'
                    ? `${totalSchools} of ${statusCounts.active || totalSchools} active synced`
                    : `${totalSchools} synced`}
                </Typography>
                <Typography variant="caption" color="#2563eb" fontWeight={600}>
                  View in report →
                </Typography>
              </Paper>
            </Grid>

            {/* Total Users */}
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                onClick={handleUserCardClick}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#3b82f6',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 18px rgba(59,130,246,0.12)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                  <PeopleAltOutlinedIcon sx={{ color: '#3b82f6', fontSize: 18 }} />
                  <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.7rem">
                    {clientStatusTab === 'active' ? 'Active Users (Past 7 Days)' : 'Total Users'}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={900} color="#0f172a">
                  {clientStatusTab === 'active' && dashboardData.pastWeekActiveUsers != null
                    ? Number(dashboardData.pastWeekActiveUsers).toLocaleString('en-IN')
                    : Number(dashboardData.totalClientUsers || 0).toLocaleString('en-IN')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {clientStatusTab === 'active'
                    ? `${Number(dashboardData.pastWeekStaffActive || 0).toLocaleString('en-IN')} staff · ${Number(dashboardData.pastWeekStudentActive || 0).toLocaleString('en-IN')} students active`
                    : `${Number(dashboardData.pastWeekActiveUsers || 0).toLocaleString('en-IN')} active in past 7 days`}
                </Typography>
              </Paper>
            </Grid>

            {/* Staff Users */}
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                onClick={handleUserCardClick}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#e11d48',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 18px rgba(225,29,72,0.12)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                  <BadgeOutlinedIcon sx={{ color: '#e11d48', fontSize: 18 }} />
                  <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.7rem">
                    {clientStatusTab === 'active' ? 'Active Staff (7 Days)' : 'Staff Users'}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={900} color="#e11d48">
                  {clientStatusTab === 'active' && dashboardData.pastWeekStaffActive != null
                    ? Number(dashboardData.pastWeekStaffActive).toLocaleString('en-IN')
                    : Number(dashboardData.staffUsers || 0).toLocaleString('en-IN')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {clientStatusTab === 'active' ? 'Logged in past 7 days' : 'Teachers & Admin'}
                </Typography>
              </Paper>
            </Grid>

            {/* Student Users */}
            <Grid item xs={6} sm={3}>
              <Paper
                elevation={0}
                onClick={handleUserCardClick}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#16a34a',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 18px rgba(22,163,74,0.12)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={0.8} mb={0.5}>
                  <FaceOutlinedIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                  <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.7rem">
                    {clientStatusTab === 'active' ? 'Active Students (7 Days)' : 'Student Users'}
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={900} color="#15803d">
                  {clientStatusTab === 'active' && dashboardData.pastWeekStudentActive != null
                    ? Number(dashboardData.pastWeekStudentActive).toLocaleString('en-IN')
                    : Number(dashboardData.studentUsers || 0).toLocaleString('en-IN')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {clientStatusTab === 'active' ? 'Active in past 7 days' : 'Enrolled Students'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* ============================================================== */}
      {/* 3. CATEGORIZED MODULE ADOPTION CARDS (CLICK TO INSPECT)        */}
      {/* ============================================================== */}
      <Box mb={3.5}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box>
            <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
              Module Adoption Breakdown
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click any card to inspect exactly which schools have not used the feature
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* CATEGORY 1: Academic & Student Core Modules */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                backgroundColor: '#ffffff',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <MenuBookOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={800} color="#1e293b">
                  Academic Core Modules
                </Typography>
              </Box>

              <Grid container spacing={1.5}>
                {MODULE_DEFINITIONS.filter((m) => m.category === 'academic').map((mod) => {
                  const IconComp = mod.icon;
                  const neverUsed = Number(neverUsedCounts[mod.key] || 0);
                  const activeSchools = Math.max(0, totalSchools - neverUsed);
                  const rate = totalSchools > 0 ? Math.round((activeSchools / totalSchools) * 100) : 0;

                  return (
                    <Grid item xs={12} sm={6} key={mod.key}>
                      <Card
                        elevation={0}
                        onClick={() => handleOpenModuleDrawer(mod)}
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          border: '1px solid',
                          borderColor: neverUsed > 0 ? '#fed7aa' : '#bbf7d0',
                          backgroundColor: neverUsed > 0 ? '#fffbeb' : '#f0fdf4',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.06)',
                            borderColor: mod.accentColor,
                          },
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Box display="flex" alignItems="center" gap={0.8}>
                            <IconComp sx={{ color: mod.accentColor, fontSize: 18 }} />
                            <Typography variant="body2" fontWeight={700} color="#0f172a" noWrap>
                              {mod.shortLabel}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={800} color={rate >= 70 ? '#15803d' : '#ea580c'}>
                            {rate}%
                          </Typography>
                        </Box>

                        <LinearProgress
                          variant="determinate"
                          value={rate}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#e2e8f0',
                            mb: 1.2,
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              backgroundColor: rate >= 70 ? '#22c55e' : '#f97316',
                            },
                          }}
                        />

                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={0.5} flexWrap="wrap">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Tooltip title="Click to view institutions actively using this module">
                              <Chip
                                size="small"
                                clickable
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenModuleDrawer(mod, 'used');
                                }}
                                label={`Used: ${activeSchools}`}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  bgcolor: '#dcfce7',
                                  color: '#166534',
                                  border: '1px solid #86efac',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: '#bbf7d0' },
                                }}
                              />
                            </Tooltip>
                            <Tooltip title="Click to view institutions that have never used this module">
                              <Chip
                                size="small"
                                clickable
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenModuleDrawer(mod, 'never_used');
                                }}
                                label={`Never Used: ${neverUsed}`}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  bgcolor: neverUsed > 0 ? '#fee2e2' : '#f1f5f9',
                                  color: neverUsed > 0 ? '#b91c1c' : '#64748b',
                                  border: neverUsed > 0 ? '1px solid #fca5a5' : '1px solid #e2e8f0',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: neverUsed > 0 ? '#fecaca' : '#e2e8f0' },
                                }}
                              />
                            </Tooltip>
                          </Stack>
                          <Typography variant="caption" color="primary" fontWeight={700} fontSize="0.7rem">
                            Inspect →
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>

          {/* CATEGORY 2: Operations & Administrative Modules */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                backgroundColor: '#ffffff',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AccountBalanceWalletOutlinedIcon sx={{ color: '#0d9488', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={800} color="#1e293b">
                  Operations & Finance Modules
                </Typography>
              </Box>

              <Grid container spacing={1.5}>
                {MODULE_DEFINITIONS.filter((m) => m.category === 'operations').map((mod) => {
                  const IconComp = mod.icon;
                  const neverUsed = Number(neverUsedCounts[mod.key] || 0);
                  const activeSchools = Math.max(0, totalSchools - neverUsed);
                  const rate = totalSchools > 0 ? Math.round((activeSchools / totalSchools) * 100) : 0;

                  return (
                    <Grid item xs={12} sm={6} key={mod.key}>
                      <Card
                        elevation={0}
                        onClick={() => handleOpenModuleDrawer(mod)}
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          border: '1px solid',
                          borderColor: neverUsed > 0 ? '#fed7aa' : '#bbf7d0',
                          backgroundColor: neverUsed > 0 ? '#fffbeb' : '#f0fdf4',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.06)',
                            borderColor: mod.accentColor,
                          },
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Box display="flex" alignItems="center" gap={0.8}>
                            <IconComp sx={{ color: mod.accentColor, fontSize: 18 }} />
                            <Typography variant="body2" fontWeight={700} color="#0f172a" noWrap>
                              {mod.shortLabel}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={800} color={rate >= 70 ? '#15803d' : '#ea580c'}>
                            {rate}%
                          </Typography>
                        </Box>

                        <LinearProgress
                          variant="determinate"
                          value={rate}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#e2e8f0',
                            mb: 1.2,
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              backgroundColor: rate >= 70 ? '#22c55e' : '#f97316',
                            },
                          }}
                        />

                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={0.5} flexWrap="wrap">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Tooltip title="Click to view institutions actively using this module">
                              <Chip
                                size="small"
                                clickable
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenModuleDrawer(mod, 'used');
                                }}
                                label={`Used: ${activeSchools}`}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  bgcolor: '#dcfce7',
                                  color: '#166534',
                                  border: '1px solid #86efac',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: '#bbf7d0' },
                                }}
                              />
                            </Tooltip>
                            <Tooltip title="Click to view institutions that have never used this module">
                              <Chip
                                size="small"
                                clickable
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenModuleDrawer(mod, 'never_used');
                                }}
                                label={`Never Used: ${neverUsed}`}
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  bgcolor: neverUsed > 0 ? '#fee2e2' : '#f1f5f9',
                                  color: neverUsed > 0 ? '#b91c1c' : '#64748b',
                                  border: neverUsed > 0 ? '1px solid #fca5a5' : '1px solid #e2e8f0',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: neverUsed > 0 ? '#fecaca' : '#e2e8f0' },
                                }}
                              />
                            </Tooltip>
                          </Stack>
                          <Typography variant="caption" color="primary" fontWeight={700} fontSize="0.7rem">
                            Inspect →
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* ============================================================== */}
      {/* 4. VISUAL CHARTS & COORDINATOR BREAKDOWN                       */}
      {/* ============================================================== */}
      <Grid container spacing={2}>
        {/* Never Used Modules Donut Chart */}
        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              backgroundColor: '#ffffff',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box mb={1}>
              <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                Never-Used Distribution
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Breakdown of unused module instances across institutions (click slice to inspect)
              </Typography>
            </Box>

            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={neverUsedData.length ? neverUsedData : [{ name: 'All Adopted', value: 1 }]}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={3}
                    cursor="pointer"
                    onClick={(entry) => {
                      if (!entry || entry.name === 'All Adopted') return;
                      const mod = MODULE_DEFINITIONS.find(
                        (m) => m.key === entry.moduleKey || m.label === entry.name || m.shortLabel === entry.name
                      );
                      if (mod) {
                        handleOpenModuleDrawer(mod, 'never_used');
                      }
                    }}
                  >
                    {(neverUsedData.length ? neverUsedData : [{ name: 'All Adopted', value: 1 }]).map(
                      (entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={neverUsedData.length ? DONUT_COLORS[index % DONUT_COLORS.length] : '#22c55e'}
                          cursor="pointer"
                          style={{ outline: 'none' }}
                        />
                      )
                    )}
                  </Pie>
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: '10px' }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Coordinator Institute Counts & Leaderboard */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              backgroundColor: '#ffffff',
              height: '100%',
            }}
          >
            <Box mb={1.5} display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                  Institutes by Coordinator
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Active portfolio size and coordinator workload
                </Typography>
              </Box>
              <Chip
                size="small"
                label={`${pocData.length} Coordinators`}
                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
              />
            </Box>

            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={pocData}
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 40, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11 }} />
                  <RechartsTooltip />
                  <Bar
                    dataKey="value"
                    name="Institutes"
                    fill="#3b82f6"
                    radius={[0, 6, 6, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ============================================================== */}
      {/* 5. IN-PAGE QUICK SCHOOLS DRAWER (ZERO CONTEXT SWITCHING)       */}
      {/* ============================================================== */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 460 },
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: drawerUsageType === 'used' ? '#f0fdf4' : '#fef2f2',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              {selectedModule && <selectedModule.icon sx={{ color: selectedModule.accentColor, fontSize: 22 }} />}
              <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
                {selectedModule?.label || 'Module Inspection'}
              </Typography>
              <Chip
                size="small"
                label={drawerUsageType === 'used' ? 'Used' : 'Never Used'}
                sx={{
                  fontWeight: 800,
                  fontSize: '0.7rem',
                  bgcolor: drawerUsageType === 'used' ? '#dcfce7' : '#fee2e2',
                  color: drawerUsageType === 'used' ? '#166534' : '#b91c1c',
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {drawerUsageType === 'used'
                ? `Showing schools actively using this module`
                : `Showing schools where this module is Never Used`}
            </Typography>
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Drawer Quick Search */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid #f1f5f9' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search school name or coordinator..."
            value={drawerSearch}
            onChange={(e) => setDrawerSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: drawerSearch ? (
                <IconButton size="small" onClick={() => setDrawerSearch('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Box>

        {/* Drawer Content */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5 }}>
          {drawerLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={1.5}>
              <CircularProgress size={32} />
              <Typography variant="caption" color="text.secondary">
                {drawerUsageType === 'used' ? 'Loading active schools...' : 'Loading inactive schools...'}
              </Typography>
            </Box>
          ) : filteredDrawerSchools.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Typography variant="body2" color="text.secondary">
                {drawerSearch
                  ? 'No matching schools found.'
                  : drawerUsageType === 'used'
                  ? 'No schools have used this module yet.'
                  : 'All schools are actively using this module! 🎉'}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredDrawerSchools.map((school, idx) => (
                <Paper
                  key={school.id || idx}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    transition: 'border-color 0.15s ease',
                    '&:hover': { borderColor: '#94a3b8' },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color="#0f172a" mb={0.3}>
                    {school.institute_name || 'Unnamed Institute'}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <PersonOutlineIcon sx={{ fontSize: 15, color: '#64748b' }} />
                    <Typography variant="caption" color="text.secondary">
                      Coordinator: <strong>{school.coordinator_name || 'Unassigned'}</strong>
                    </Typography>
                  </Box>

                  {selectedModule && school[selectedModule.schemaColumn] && (
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      {drawerUsageType === 'used' ? (
                        <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#16a34a' }} />
                      ) : (
                        <WarningAmberOutlinedIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                      )}
                      <Typography variant="caption" fontWeight={600} color={drawerUsageType === 'used' ? '#166534' : '#b91c1c'}>
                        {drawerUsageType === 'used'
                          ? `Last Activity: ${school[selectedModule.schemaColumn]}`
                          : 'Status: Never Used'}
                      </Typography>
                    </Box>
                  )}

                  {school.school_address && (
                    <Box display="flex" alignItems="flex-start" gap={0.5}>
                      <LocationOnOutlinedIcon sx={{ fontSize: 15, color: '#94a3b8', mt: 0.2 }} />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {school.school_address}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ))}
            </List>
          )}
        </Box>

        {/* Drawer Footer Actions */}
        <Box
          sx={{
            p: 1.5,
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            gap: 1,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            onClick={() => {
              setDrawerOpen(false);
              if (selectedModule) {
                handleGoToFullReport(
                  selectedModule.schemaColumn,
                  drawerUsageType === 'used' ? 'Used' : 'Never Used'
                );
              }
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              backgroundColor: '#2563eb',
              '&:hover': { backgroundColor: '#1d4ed8' },
            }}
          >
            Open in Full Report Page
          </Button>
          <Button
            variant="outlined"
            onClick={() => setDrawerOpen(false)}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Close
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}

export default ClientReportDashboard;
